/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Button, 
  CircularProgress, 
  Alert, 
  TextField 
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  Error as ErrorIcon, 
  Email as EmailIcon 
} from '@mui/icons-material';
import axios from 'axios';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';

// Helper to ensure API path is correct
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

/** Dedupe concurrent verify calls (e.g. React Strict Mode double-mount). Keep resolved promise so remounts do not re-POST. */
const verifyEmailInFlight = new Map<string, Promise<{ data: { message?: string } }>>();

const EmailVerificationPage: React.FC = () => {
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState<string>('');
  const [postVerifyRedirect, setPostVerifyRedirect] = useState<'home' | 'login'>('login');
  
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  // Extract token from URL query parameters
  useEffect(() => {
    logger.info('=== EMAIL VERIFICATION PAGE DEBUG START ===');
    logger.info('Current location:', location);
    logger.info('Search params:', location.search);
    
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const emailFromLink = queryParams.get('email') || undefined;
    
    logger.info('Extracted token:', token ? token.substring(0, 10) + '...' : 'null');
    
    if (!token) {
      logger.info('❌ NO TOKEN FOUND');
      setVerificationStatus('error');
      setMessage('No verification token found. Please check your verification link.');
      return;
    }
    
    logger.info('✅ TOKEN FOUND, making API call...');
    
    const verifyEmail = async () => {
      try {
        logger.info('📡 Making POST request to:', makeApiPath('/auth/verify-email'));
        logger.info('📡 Request body:', { token, email: emailFromLink });
        
        let response;
        if (!verifyEmailInFlight.has(token)) {
          verifyEmailInFlight.set(
            token,
            axios.post(makeApiPath('/auth/verify-email'), { token, email: emailFromLink })
          );
        }
        response = await verifyEmailInFlight.get(token)!;
        
        logger.info('✅ API Response success:', response.data);
        setVerificationStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');

        const sessionToken = localStorage.getItem('token');
        if (sessionToken) {
          setPostVerifyRedirect('home');
          try {
            axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
            await refreshUserData();
            try {
              const cached = localStorage.getItem('cached_user');
              if (cached) {
                const u = JSON.parse(cached);
                localStorage.setItem('user', JSON.stringify({ id: u.id, username: u.username, email: u.email, isEmailVerified: true }));
              }
            } catch (e) {
              logger.warn('Could not sync legacy user key:', e);
            }
          } catch (refreshErr) {
            logger.error('Failed to refresh user after verify:', refreshErr);
          }
        } else {
          try {
            const userData = localStorage.getItem('user');
            if (userData) {
              const user = JSON.parse(userData);
              user.isEmailVerified = true;
              localStorage.setItem('user', JSON.stringify(user));
              logger.info('✅ Legacy user storage updated');
            }
          } catch (storageError) {
            logger.error('Error updating local storage:', storageError);
          }
        }
        
        const isLoggedIn = !!sessionToken;
        setTimeout(() => {
          navigate(isLoggedIn ? '/' : '/login?verified=true');
        }, isLoggedIn ? 1500 : 3000);
      } catch (error: any) {
        logger.error('❌ API Response error:', error);
        logger.error('❌ Error response data:', error.response?.data);
        logger.error('❌ Error response status:', error.response?.status);
        setVerificationStatus('error');
        setMessage(error.response?.data?.message || 'Failed to verify email. The token may be invalid or expired.');
      }
    };
    
    verifyEmail();
  }, [location, navigate, refreshUserData]);
  
  // Handle resend verification email
  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setResendStatus('error');
      setResendMessage('Please enter your email address');
      return;
    }
    
    setResendStatus('loading');
    
    try {
      const response = await axios.post(makeApiPath('/auth/resend-verification'), { email });
      setResendStatus('success');
      setResendMessage(response.data.message || 'Verification email sent successfully!');
    } catch (error: any) {
      setResendStatus('error');
      setResendMessage(error.response?.data?.message || 'Failed to resend verification email');
    }
  };
  
  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Email Verification
          </Typography>
        </Box>
        
        {/* Verification Status */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {verificationStatus === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="body1">
                Verifying your email address...
              </Typography>
            </>
          )}
          
          {verificationStatus === 'success' && (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
              <Typography variant="body1" paragraph>
                {postVerifyRedirect === 'home'
                  ? 'You will be redirected home shortly.'
                  : 'You will be redirected to the login page shortly.'}
              </Typography>
              <Button 
                component={RouterLink} 
                to={postVerifyRedirect === 'home' ? '/' : '/login?verified=true'}
                variant="contained" 
                color="primary"
              >
                {postVerifyRedirect === 'home' ? 'Go to home' : 'Go to Login'}
              </Button>
            </>
          )}
          
          {verificationStatus === 'error' && (
            <>
              <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
              <Alert severity="error" sx={{ mb: 2 }}>
                {message}
              </Alert>
              
              <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Need a new verification link?
                </Typography>
                
                <Box component="form" onSubmit={handleResendVerification} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                  />
                  
                  {resendStatus === 'error' && (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                      {resendMessage}
                    </Alert>
                  )}
                  
                  {resendStatus === 'success' && (
                    <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                      {resendMessage}
                    </Alert>
                  )}
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={resendStatus === 'loading'}
                    startIcon={resendStatus === 'loading' ? <CircularProgress size={20} /> : <EmailIcon />}
                  >
                    {resendStatus === 'loading' ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                </Box>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Button 
                  component={RouterLink} 
                  to="/login" 
                  variant="outlined"
                >
                  Back to Login
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default EmailVerificationPage; 